/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { Else, If, Then } from "react-if";
import styled from "styled-components";
import { Sidebar, Text, theme } from "@nordcloud/gnui";
import {
  CloudProviderType,
  Maybe,
  useQueryAccountsV2ByProviderQuery,
} from "~/generated/graphql";
import { Pagination, PaginationState } from "~/components/Pagination";
import { UniversalWrap } from "~/components/Utils";
import { useQueryState } from "~/hooks";
import { isNotEmpty } from "~/tools";
import { AccountSelectorItemList } from "./AccountSelectorItemList";

const Footer = styled.div`
  flex: 1;
`;

type Props = {
  onClose: () => void;
  isOpen: boolean;
  onSubmit: (id: string, provider?: Maybe<CloudProviderType>) => void;
  title: string;
};

export function AccountSelector({ onClose, isOpen, onSubmit, title }: Props) {
  const {
    state: { limit, page },
  } = useQueryState<PaginationState>();
  const { data, loading, error } = useQueryAccountsV2ByProviderQuery({
    variables: {
      limit,
      page,
      filter: {
        providerTypes: [
          CloudProviderType.Kubernetes,
          CloudProviderType.Openshift,
        ],
      },
    },
  });

  const accounts = data?.accountsV2?.accounts ?? [];

  return (
    <Sidebar
      isOpen={isOpen}
      title={title}
      onClick={onClose}
      footer={
        <Footer>
          <Pagination small count={data?.accountsV2?.count ?? 0} />
        </Footer>
      }
    >
      <UniversalWrap
        loaderProps={{
          loading,
          inContent: true,
        }}
        errorProps={{
          error,
        }}
      >
        <If condition={isNotEmpty(accounts)}>
          <Then>
            <AccountSelectorItemList items={accounts} onSubmit={onSubmit} />
          </Then>
          <Else>
            <Text
              css={{
                paddingTop: theme.spacing.spacing04,
                paddingBottom: theme.spacing.spacing04,
              }}
            >
              No accounts matching selected criteria
            </Text>
          </Else>
        </If>
      </UniversalWrap>
    </Sidebar>
  );
}
